import router from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { usePlayerStore } from '../../store'
import { getParameterByName, getPpid, prettyLog, sendGA4 } from '../../utils'
import { usePage, useSiteMeta } from './usePage'

const STORAGE_CONSENT = true

function getConsentToStorage() {
  return STORAGE_CONSENT
}

function shouldLogPal() {
  return getParameterByName('logPal') === 'true'
}

function palNonceRequestSnapshot(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: InstanceType<any>
) {
  return {
    adWillAutoPlay: request.adWillAutoPlay,
    adWillPlayMuted: request.adWillPlayMuted,
    continuousPlayback: request.continuousPlayback,
    descriptionUrl: request.descriptionUrl,
    iconsSupported: request.iconsSupported,
    playerType: request.playerType,
    playerVersion: request.playerVersion,
    ppid: request.ppid,
    sessionId: request.sessionId,
    supportedApiFrameworks: request.supportedApiFrameworks,
    url: request.url,
    videoHeight: request.videoHeight,
    videoWidth: request.videoWidth,
  }
}
type TNonceRequest = {
  nonceUrl: string
  auth?: string
  content?: string
  signals?: {
    [key: string]: string | number | boolean | null
  }
}
type NonceManager = {
  getNonce: () => string | null
}
type NonceLoader = {
  loadNonceManager: (request: TNonceRequest) => Promise<NonceManager>
}
type UseGooglePalProps = {
  adWillAutoPlay?: boolean
  adWillPlayMuted?: boolean
  kalturaReady?: boolean
  continuousPlayback?: boolean
  // Set to false while the caller doesn't know yet whether this asset is
  // live or VOD (continuousPlayback would only be a guess). Defaults to
  // true so callers with no such distinction fire immediately as before.
  continuousPlaybackKnown?: boolean
  // Pass the mounted player/video element once it exists so PAL gets its
  // real rendered size. Must be a plain element (state), not a ref: a ref's
  // identity never changes when `.current` is populated, so it would never
  // re-trigger the request effect below once the player actually mounts.
  videoElement?: HTMLElement | null
}

export const useGooglePal = ({
  adWillAutoPlay = false,
  adWillPlayMuted = false,
  kalturaReady = true,
  continuousPlayback = false,
  continuousPlaybackKnown = true,
  videoElement,
}: UseGooglePalProps = {}) => {
  const pageData = usePage()
  const SiteMeta = useSiteMeta()
  const googlePalTimeout = useRef<null | NodeJS.Timeout>(null)
  const [googlePalReady] = usePlayerStore(state => [state.googlePalReady])

  // Move module-level variables inside the hook
  const nonceLoaderRef = useRef<NonceLoader | null>(null)
  const managerPromiseRef = useRef<Promise<NonceManager> | null>(null)
  const sessionIdRef = useRef<string | null>(null)

  const [nonceManager, setNonceManager] = useState<NonceManager>()

  const [pal, setPal] = useState<string | null>(
    SiteMeta?.config?.player?.sendPal === 'off' ? 'off' : ''
  )

  // getBoundingClientRect() right when videoElement mounts can still read
  // 0x0 (its aspect-ratio box hasn't been laid out yet), which would slip
  // through as a "real" measurement of zero and fall back to the window
  // size anyway. Wait for an actual non-zero layout via ResizeObserver
  // instead of trusting a single synchronous read.
  const [videoSize, setVideoSize] = useState<{
    width: number
    height: number
  } | null>(null)

  useEffect(() => {
    if (!videoElement) {
      setVideoSize(null)
      return
    }

    let settled = false
    const applyRect = (rect: { width: number; height: number }) => {
      if (rect.width > 0 && rect.height > 0) {
        settled = true
        setVideoSize({
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        })
      }
    }

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0]
      if (entry) {
        applyRect(entry.contentRect)
      }
    })
    resizeObserver.observe(videoElement)

    // ResizeObserver is spec'd to report the current size right after
    // observe(), but don't let a real, mounted player go unmeasured forever
    // if that somehow doesn't happen - fall back to a direct read.
    const fallbackTimer = setTimeout(() => {
      if (!settled) {
        applyRect(videoElement.getBoundingClientRect())
      }
    }, 1500)

    return () => {
      resizeObserver.disconnect()
      clearTimeout(fallbackTimer)
    }
  }, [videoElement])

  useEffect(() => {
    // Initialize NonceLoader when Google PAL is ready
    if (googlePalReady && window.goog && !nonceLoaderRef.current) {
      const consentSettings = new window.goog.pal.ConsentSettings()
      consentSettings.allowStorage = getConsentToStorage()

      nonceLoaderRef.current = new window.goog.pal.NonceLoader(consentSettings)
    }
  }, [googlePalReady])

  const setPalUndefined = useCallback(() => {
    if (googlePalTimeout.current) {
      clearTimeout(googlePalTimeout.current)
    }
    setPal('undefined')
    if (pageData) {
      sendGA4({
        eventName: 'pal_empty',
        pageData: pageData,
      })
    }
  }, [pageData])

  useEffect(() => {
    let cancelled = false

    const generateNonce = () => {
      if (
        !(
          googlePalReady &&
          kalturaReady &&
          window.goog &&
          nonceLoaderRef.current
        )
      ) {
        return
      }

      // videoElement !== undefined means the caller wants the real player
      // size reported. Wait for it to actually mount AND be laid out
      // (videoSize) rather than requesting a nonce with the wrong (window)
      // size. videoElement === undefined means the caller has no player to
      // report, so proceed as before.
      if (videoElement !== undefined && !videoSize) {
        return
      }

      // Wait until the caller actually knows if this is live or VOD, rather
      // than requesting with a guessed continuousPlayback and re-requesting
      // once the real value is known.
      if (!continuousPlaybackKnown) {
        return
      }

      if (cancelled) {
        return
      }

      const request = new window.goog.pal.NonceRequest()
      request.adWillAutoPlay = adWillAutoPlay
      request.adWillPlayMuted = adWillPlayMuted
      request.continuousPlayback = continuousPlayback
      request.descriptionUrl = window.location.origin + window.location.pathname
      request.iconsSupported = false
      request.playerType = '13tvplayer'
      // Assigning undefined crashes the real PAL SDK's internal setter
      // (it assumes a string), so only set this once Kaltura has loaded.
      if (window.KalturaPlayer?.VERSION) {
        request.playerVersion = window.KalturaPlayer.VERSION
      }
      // Same alphanumeric-only ppid as the display/GPT and video IMA calls,
      // so GAM can correlate the same user across ad formats.
      request.ppid = getPpid()
      if (!sessionIdRef.current) {
        sessionIdRef.current = uuidv4()
      }
      request.sessionId = sessionIdRef.current
      request.supportedApiFrameworks = []
      request.url = window.location.href
      // Use the actual rendered player size, not the window size, so PAL
      // sees the real video dimensions.
      request.videoWidth = videoSize ? videoSize.width : window.innerWidth
      request.videoHeight = videoSize ? videoSize.height : window.innerHeight

      if (shouldLogPal()) {
        prettyLog('pal_nonce_request', palNonceRequestSnapshot(request), 'info')
      }

      managerPromiseRef.current =
        nonceLoaderRef.current.loadNonceManager(request)

      managerPromiseRef.current
        ?.then((manager: NonceManager) => {
          if (cancelled) {
            return
          }
          if (shouldLogPal()) {
            const nonce = manager.getNonce()
            prettyLog(
              'pal_nonce',
              { nonce, length: nonce?.length ?? 0 },
              'info'
            )
          }
          setNonceManager(manager)
        })
        .catch((error: string) => {
          if (cancelled) {
            return
          }
          prettyLog(
            'useGooglePal: 95',
            { msg: 'Error generating nonce', error },
            'error'
          )
        })
    }

    void generateNonce()

    return () => {
      cancelled = true
    }
  }, [
    googlePalReady,
    kalturaReady,
    adWillAutoPlay,
    adWillPlayMuted,
    continuousPlayback,
    continuousPlaybackKnown,
    videoElement,
    videoSize,
  ])

  useEffect(() => {
    if (SiteMeta?.config?.player?.sendPal !== 'off') {
      const onRouteChangeStart = () => {
        googlePalTimeout.current = setTimeout(setPalUndefined, 500)
        try {
          if (nonceManager) {
            setPal(nonceManager.getNonce())
          }
          if (googlePalTimeout.current) {
            clearTimeout(googlePalTimeout.current)
          }
        } catch (e) {
          setPalUndefined()
        }
      }

      if (nonceManager) {
        const nonce = nonceManager.getNonce()
        if (shouldLogPal()) {
          prettyLog('pal', { pal: nonce }, 'info')
        }
        setPal(nonce)
      }

      router.events.on('routeChangeStart', onRouteChangeStart)

      return () => {
        router.events.off('routeChangeStart', onRouteChangeStart)
      }
    }
  }, [SiteMeta?.config?.player?.sendPal, nonceManager, setPalUndefined])

  return pal
}
